import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';
import { useTheme } from '../context/ThemeContext';
import './MyInvoices.css';

// Get the main platform URL based on environment
const getMainPlatformUrl = () => {
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:3000';
  }
  return 'https://ai.app.platform.halisoft.biz';
};

const MyInvoices = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [user, setUser] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Theme colors (for future use)
  const colors = {
    cardBg: isDark ? '#1e293b' : 'white',
    textPrimary: isDark ? '#f3f4f6' : '#1f2937',
    textSecondary: isDark ? '#9ca3af' : '#6b7280'
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);

      // Get email from URL first, then localStorage
      const urlParams = new URLSearchParams(window.location.search);
      let email = urlParams.get('email');
      if (!email) email = localStorage.getItem('halisoft_email');
      if (!email) {
        const storedUser = localStorage.getItem('halisoft_user');
        if (storedUser) {
          try { email = JSON.parse(storedUser)?.email; } catch (e) {}
        }
      }
      if (email) localStorage.setItem('halisoft_email', email);

      // Load user and subscription data
      const [userRes, subRes] = await Promise.all([
        api.get('/api/check-auth').catch(() => ({ data: { user: { email: email || 'Anonymous' } } })),
        email ? api.get(`/api/subscriptions/current?email=${encodeURIComponent(email)}`).catch(() => ({ data: null })) : Promise.resolve({ data: null })
      ]);
      
      const userData = userRes.data?.user || { email: email || 'Anonymous' };
      if (subRes.data && subRes.data.plan_id !== 'free') {
        userData.subscription = { plan: subRes.data, status: subRes.data.status || 'active' };
      }
      setUser(userData);
      
      // No invoices yet (will be added when subscription system is integrated)
      setInvoices([]);
    } catch (err) {
      console.error('Error loading data:', err);
      if (err.response?.status === 401) {
        navigate('/login');
      } else {
        setError('Error loading invoices');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDownloadInvoice = async (invoiceId) => {
    try {
      const response = await api.get(`/api/invoices/${invoiceId}/pdf`, {
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${invoiceId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Error downloading invoice:', err);
      alert('Error downloading invoice');
    }
  };

  const handleLogout = async () => {
    try {
      await api.post('/api/auth/logout');
      localStorage.removeItem('halisoft_user');
      navigate('/login');
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      PAID: { text: 'Paid', class: 'paid' },
      PENDING: { text: 'Pending', class: 'pending' },
      FAILED: { text: 'Failed', class: 'failed' },
      REFUNDED: { text: 'Refunded', class: 'refunded' }
    };

    return badges[status] || { text: status, class: 'unknown' };
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const calculateTotals = () => {
    const paidInvoices = invoices.filter(inv => inv.status === 'PAID');
    const total = paidInvoices.reduce((sum, inv) => sum + inv.amount, 0);
    return {
      total,
      count: paidInvoices.length,
      thisMonth: paidInvoices.filter(inv => {
        const invDate = new Date(inv.date);
        const now = new Date();
        return invDate.getMonth() === now.getMonth() &&
               invDate.getFullYear() === now.getFullYear();
      }).reduce((sum, inv) => sum + inv.amount, 0)
    };
  };

  if (loading) {
    return (
      <div className="invoices-loading">
        <div className="spinner-large"></div>
        <p>Loading invoices...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="invoices-error">
        <p>{error}</p>
        <button onClick={loadData} className="btn-retry">
          Retry
        </button>
      </div>
    );
  }

  const totals = calculateTotals();

  return (
    <div className="my-invoices">
      <div className="invoices-container">
        {/* Summary Cards */}
        <div className="summary-cards">
          <div className="summary-card">
            <div className="summary-icon">💰</div>
            <div className="summary-content">
              <h3>Total Spent</h3>
              <p className="summary-value">{formatCurrency(totals.total)}</p>
              <p className="summary-detail">{totals.count} paid invoices</p>
            </div>
          </div>

          <div className="summary-card">
            <div className="summary-icon">📅</div>
            <div className="summary-content">
              <h3>This Month</h3>
              <p className="summary-value">{formatCurrency(totals.thisMonth)}</p>
              <p className="summary-detail">Current month spending</p>
            </div>
          </div>

          <div className="summary-card">
            <div className="summary-icon">📊</div>
            <div className="summary-content">
              <h3>Status</h3>
              <p className="summary-value">{user?.subscription?.plan?.name || 'FREE'}</p>
              <p className="summary-detail">Current plan</p>
            </div>
          </div>
        </div>

        {/* Invoices Table */}
        <div className="invoices-section">
          <div className="section-header">
            <h2>📄 Invoice History</h2>
            <p className="section-subtitle">
              All your PayPal invoices in one place
            </p>
          </div>

          {invoices.length === 0 ? (
            <div className="no-invoices">
              <div className="no-invoices-icon">📭</div>
              <h3>No Invoices</h3>
              <p>You don't have any invoices yet.</p>
              <p>Invoices will appear here after your payments.</p>
              <button
                onClick={() => navigate('/subscription')}
                className="btn-subscribe"
              >
                View Plans
              </button>
            </div>
          ) : (
            <div className="invoices-table-container">
              <table className="invoices-table">
                <thead>
                  <tr>
                    <th>Number</th>
                    <th>Date</th>
                    <th>Description</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice) => {
                    const badge = getStatusBadge(invoice.status);
                    return (
                      <tr key={invoice.id}>
                        <td className="invoice-number">
                          #{invoice.invoiceNumber || invoice.id}
                        </td>
                        <td className="invoice-date">
                          {formatDate(invoice.date)}
                        </td>
                        <td className="invoice-description">
                          <div className="description-content">
                            <strong>{invoice.description}</strong>
                            {invoice.plan && (
                              <span className="plan-tag">
                                {invoice.plan}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="invoice-amount">
                          {formatCurrency(invoice.amount, invoice.currency)}
                        </td>
                        <td className="invoice-status">
                          <span className={`status-badge ${badge.class}`}>
                            {badge.text}
                          </span>
                        </td>
                        <td className="invoice-actions">
                          <button
                            onClick={() => handleDownloadInvoice(invoice.id)}
                            className="btn-download"
                            title="Download PDF"
                          >
                            💾 PDF
                          </button>
                          {invoice.paypalUrl && (
                            <a
                              href={invoice.paypalUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn-view"
                              title="View on PayPal"
                            >
                              👁️ PayPal
                            </a>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Payment Method Info */}
        {invoices.length > 0 && (
          <div className="payment-info">
            <h3>💳 Payment Method</h3>
            <p>
              All payments are securely processed by PayPal.
              Your banking information is never stored on our servers.
            </p>
            <div className="payment-features">
              <div className="feature">
                <span className="feature-icon">🔒</span>
                <span>SSL Encryption</span>
              </div>
              <div className="feature">
                <span className="feature-icon">🛡️</span>
                <span>Buyer Protection</span>
              </div>
              <div className="feature">
                <span className="feature-icon">✓</span>
                <span>Secure Payment</span>
              </div>
            </div>
          </div>
        )}

        {/* Help Section */}
        <div className="help-section">
          <h3>❓ Need Help?</h3>
          <div className="help-grid">
            <div className="help-item">
              <h4>Question about an invoice?</h4>
              <p>Contact our support at support@halisoft.com</p>
            </div>
            <div className="help-item">
              <h4>Refund request?</h4>
              <p>Refunds are processed within 5-7 business days</p>
            </div>
            <div className="help-item">
              <h4>Recurring billing</h4>
              <p>Your invoices are automatically generated each month</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyInvoices;
